import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useAction, useMutation } from 'convex/react'
import { api } from '../../../../../convex/_generated/api'
import { PlyrPlayer } from '@/components/video'
import {
  ArrowLeft, Eye, Loader2, FolderOpen, ArrowRight, CheckCircle,
  MessageSquare, Wrench, Film, Clock, Star,
  Play, Download, AlertTriangle, Users, FileText, ExternalLink,
  Snowflake, X,
} from 'lucide-react'

const PLAN_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  admin: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Admin' },
  free: { bg: 'bg-neutral-500/10', text: 'text-neutral-400', label: 'Free' },
  pro: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Pro' },
  max: { bg: 'bg-violet-500/10', text: 'text-violet-400', label: 'Max' },
  ultra: { bg: 'bg-fuchsia-500/10', text: 'text-fuchsia-400', label: 'Ultra' },
  'pro-trialing': { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Pro Trial' },
  'pro-past_due': { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Pro Past Due' },
  'max-past_due': { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Max Past Due' },
  'ultra-past_due': { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'Ultra Past Due' },
  'pro-incomplete': { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Pro Incomplete' },
  'max-incomplete': { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Max Incomplete' },
  'ultra-incomplete': { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Ultra Incomplete' },
}

function formatDuration(ms: number): string {
  if (ms <= 0) return '--'
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function PlanBadge({ plan }: { plan: string }) {
  const style = PLAN_STYLES[plan] ?? PLAN_STYLES.free
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  )
}

function Stat({ icon: Icon, label, value, sub }: { icon: any; label: string; value: string | number; sub?: string }) {
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <Icon className="h-3.5 w-3.5 text-neutral-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-neutral-500">{label}</div>
        <div className="text-sm font-medium text-neutral-900">{value}</div>
        {sub && <div className="text-[11px] text-neutral-400">{sub}</div>}
      </div>
    </div>
  )
}

function AnalysisLog({ content }: { content: string }) {
  const lines = content.split('\n').filter((l: string) => l.trim())
  return (
    <div className="rounded-lg bg-neutral-900 p-4 font-mono text-xs space-y-1 overflow-y-auto max-h-[600px]">
      {lines.map((line: string, i: number) => {
        const isInProgress = (i === lines.length - 1) && !line.includes('[ERROR]') && !line.includes('[FATAL]') && !line.includes('Analysis complete')
        const isError = line.includes('[ERROR]') || line.includes('[FATAL]')
        const isWarn = line.includes('[WARN]')
        const isStepLine = line.match(/^\[(\d)\/(\d)\]/)
        const isDetail = line.startsWith('  ')
        return (
          <div key={i} className="flex items-start gap-2">
            {isInProgress ? (
              <Loader2 className="h-3 w-3 animate-spin text-amber-400 shrink-0 mt-0.5" />
            ) : isError ? (
              <AlertTriangle className="h-3 w-3 text-red-400 shrink-0 mt-0.5" />
            ) : isStepLine ? (
              <CheckCircle className="h-3 w-3 text-green-400 shrink-0 mt-0.5" />
            ) : (
              <span className="w-3 shrink-0" />
            )}
            <span className={
              isInProgress ? 'text-amber-300' :
              isError ? 'text-red-400' :
              isWarn ? 'text-yellow-400' :
              isStepLine ? 'text-green-300' :
              isDetail ? 'text-neutral-500' :
              'text-neutral-400'
            }>
              {line}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function ChatLog({ content }: { content: string }) {
  const lines = content.split('\n')
  return (
    <div className="rounded-lg bg-neutral-900 p-4 font-mono text-xs space-y-0 overflow-y-auto max-h-[600px]">
      {lines.map((line: string, i: number) => {
        const isUser = /^\[[\d:]+\]\s*(\([\d:]+\)\s*)?USER?:/i.test(line)
        const isAgent = /^\[[\d:]+\]\s*(\([\d:]+\)\s*)?AGENT?:/i.test(line)
        const isSystem = /^\[[\d:]+\]\s*(\([\d:]+\)\s*)?SYSTEM:/i.test(line)
        const isToolCall = line.match(/^\s+> tool_call:/)
        const isToolResult = line.match(/^\s+< tool_result/)
        const isToolInput = line.match(/^\s{4}/)
        const isEmpty = line.trim() === ''
        return (
          <div key={i} className={isEmpty ? 'h-2' : undefined}>
            <span className={
              isUser ? 'text-cyan-400 font-bold' :
              isAgent ? 'text-green-400 font-bold' :
              isSystem ? 'text-yellow-400 font-bold' :
              isToolCall ? 'text-violet-400' :
              isToolResult ? 'text-amber-400' :
              isToolInput ? 'text-neutral-500' :
              'text-neutral-300'
            }>
              {line}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function ConsoleBlock({ content, className }: { content: string; className?: string }) {
  const lines = content.split('\n')
  return (
    <div className={`rounded-lg bg-neutral-900 p-4 font-mono text-xs space-y-0 overflow-y-auto max-h-[600px] ${className || ''}`}>
      {lines.map((line: string, i: number) => {
        const isEmpty = line.trim() === ''
        const isLabel = /^(Session:|User:|What happened:|Issues:|No issues\.|\* Analyzed)/.test(line)
        const isIssue = /^\s*\d+\.\s/.test(line)
        const isSectionHeader = /^>>>/.test(line)
        const isMetaKey = /^\w[\w_]+:\s/.test(line) && !isLabel
        const isBullet = /^\s*[-*]\s+\*\*/.test(line)
        return (
          <div key={i} className={isEmpty ? 'h-2' : undefined}>
            <span className={
              isSectionHeader ? 'text-violet-400' :
              isLabel ? 'text-neutral-500' :
              isIssue ? 'text-amber-400/70' :
              isMetaKey ? 'text-neutral-500' :
              isBullet ? 'text-neutral-400' :
              'text-neutral-300'
            }>
              {line}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 7 ? 'text-emerald-700 bg-emerald-50'
    : score >= 4 ? 'text-amber-700 bg-amber-50'
    : 'text-red-700 bg-red-50'
  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${color}`}>
      Analysis Score: {score}/10
    </span>
  )
}

function SeverityBadge({ severity }: { severity: 'minor' | 'moderate' | 'severe' }) {
  const style = severity === 'severe' ? 'bg-red-100 text-red-700'
    : severity === 'moderate' ? 'bg-amber-100 text-amber-700'
    : 'bg-neutral-100 text-neutral-600'
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${style}`}>{severity}</span>
}

function DimensionScores({ dimensions }: { dimensions: Array<{ name: string; score: number; weight: number; reasoning: string }> }) {
  return (
    <div className="space-y-1.5">
      {dimensions.map((d, i) => {
        const pct = Math.round(d.score * 100)
        const barColor = pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-36 text-neutral-600 shrink-0">{d.name.replace(/_/g, ' ')}</span>
            <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="w-10 text-right font-mono text-neutral-500">{pct}%</span>
          </div>
        )
      })}
    </div>
  )
}

function IssuesList({ issues }: { issues: Array<{ category: string; severity: 'minor' | 'moderate' | 'severe'; description: string; evidence: string; timestampMs?: number }> }) {
  const severe = issues.filter(i => i.severity === 'severe')
  const moderate = issues.filter(i => i.severity === 'moderate')
  const minor = issues.filter(i => i.severity === 'minor')
  const groups = [
    { label: 'Severe', items: severe },
    { label: 'Moderate', items: moderate },
    { label: 'Minor', items: minor },
  ].filter(g => g.items.length > 0)

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group.label}>
          <div className="text-[11px] font-medium text-neutral-400 uppercase tracking-wide mb-1">{group.label} ({group.items.length})</div>
          <div className="space-y-1.5">
            {group.items.map((issue, i) => (
              <div key={i} className="flex items-start gap-2 text-xs rounded-md bg-neutral-50 px-2.5 py-2 border border-neutral-100">
                <SeverityBadge severity={issue.severity} />
                <div className="min-w-0">
                  <span className="text-neutral-400 font-mono text-[10px]">{issue.category}</span>
                  <div className="text-neutral-700">{issue.description}</div>
                  <div className="text-neutral-400 mt-0.5 italic">"{issue.evidence}"</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function formatFrameTimestamp(ms: number): string {
  const sec = Math.floor(ms / 1000)
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function FreezeModal({
  workflowId,
  messageIndex,
  defaultName,
  onClose,
}: {
  workflowId: string
  messageIndex: number
  defaultName: string
  onClose: () => void
}) {
  const createSnapshot = useMutation(api.evals.createSnapshot)
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  const submit = async () => {
    setError(null)
    setSubmitting(true)
    try {
      const id = await createSnapshot({
        sourceWorkflowId: workflowId as any,
        messageIndex,
        name: defaultName,
        description: note.trim() || undefined,
        criteria: [],
      })
      setResult(String(id))
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create snapshot')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-xl shadow-xl border border-neutral-200" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <Snowflake className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium">Freeze at message {messageIndex}</span>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700"><X className="h-4 w-4" /></button>
        </div>

        {result ? (
          <div className="p-5 space-y-3">
            <div className="text-sm text-neutral-700">Snapshot created{note.trim() ? ' and issue filed.' : '.'}</div>
            <div className="text-xs font-mono bg-neutral-50 border border-neutral-200 rounded px-2 py-1.5">{result}</div>
            <div className="text-xs text-neutral-500">
              Replay from CLI: <code className="font-mono text-neutral-700">bun scripts/admin.ts replay {result}</code>
            </div>
            <div className="flex gap-2 pt-2">
              {note.trim() && (
                <Link to="/admin/issues" className="text-xs px-3 py-1.5 rounded bg-neutral-900 text-white hover:bg-neutral-800">View issues</Link>
              )}
              <Link to="/admin/evals" className="text-xs px-3 py-1.5 rounded bg-neutral-900 text-white hover:bg-neutral-800">Open evals dashboard</Link>
              <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-neutral-200 hover:bg-neutral-50">Close</button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className="text-[11px] font-medium text-neutral-500 uppercase tracking-wide">What's going wrong here?</label>
              <textarea
                autoFocus
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={4}
                placeholder="Describe what the agent did wrong at this point..."
                className="mt-1 w-full text-sm px-3 py-2 border border-neutral-200 rounded focus:outline-none focus:border-neutral-400 resize-none"
              />
            </div>

            {error && <div className="text-xs text-red-600">{error}</div>}

            <div className="flex gap-2 pt-1">
              <button onClick={submit} disabled={submitting} className="text-xs px-3 py-1.5 rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50">
                {submitting ? 'Creating…' : 'Create snapshot'}
              </button>
              <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-neutral-200 hover:bg-neutral-50">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function MessagesTab({ workflowId }: { workflowId: string }) {
  const messages = useQuery(api.admin.getProjectMessages, { workflowId })
  const [expanded, setExpanded] = useState<number | null>(null)
  const [freezeAt, setFreezeAt] = useState<number | null>(null)

  if (messages === undefined) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="h-4 w-4 animate-spin text-neutral-400" /></div>
  }
  if (!messages.length) {
    return <div className="text-center py-12 text-sm text-neutral-400">No messages</div>
  }

  return (
    <div className="space-y-1">
      <div className="text-[11px] text-neutral-400 mb-2">{messages.length} messages — click <Snowflake className="inline h-3 w-3 mx-0.5 text-blue-500" /> to freeze project state at that point as an eval snapshot</div>
      {messages.map((m: any, i: number) => {
        const role = m.role as string
        const roleStyle =
          role === 'user' ? 'bg-cyan-50 text-cyan-700' :
          role === 'assistant' ? 'bg-emerald-50 text-emerald-700' :
          'bg-amber-50 text-amber-700'
        const toolCount = m.toolCalls?.length ?? 0
        const isExpanded = expanded === i
        return (
          <div key={m._id} className="rounded-lg border border-transparent hover:border-neutral-200 hover:bg-neutral-50 px-2 py-1.5 group">
            <div className="flex items-start gap-2">
              <span className="text-[10px] font-mono text-neutral-400 w-7 mt-0.5 shrink-0 text-right">{i}</span>
              <span className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 ${roleStyle}`}>{role}</span>
              <button onClick={() => setExpanded(isExpanded ? null : i)} className="text-xs text-left flex-1 min-w-0 truncate text-neutral-700 hover:text-neutral-900">
                {m.content?.slice(0, 200) || '(no text)'}
              </button>
              {toolCount > 0 && (
                <span className="text-[10px] bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded shrink-0">{toolCount} tool{toolCount !== 1 ? 's' : ''}</span>
              )}
              <button
                onClick={() => setFreezeAt(i + 1)}
                title={`Freeze at message ${i + 1} (everything 0..${i} is the snapshot, agent re-runs from there)`}
                className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-1.5 py-0.5 rounded shrink-0"
              >
                <Snowflake className="h-3 w-3" /> Freeze
              </button>
            </div>
            {isExpanded && (
              <div className="mt-2 ml-9 space-y-1.5">
                <pre className="text-xs whitespace-pre-wrap text-neutral-700 bg-neutral-50 rounded p-2 max-h-48 overflow-y-auto">{m.content}</pre>
                {m.toolCalls?.length > 0 && (
                  <div className="space-y-1">
                    {m.toolCalls.map((tc: any, j: number) => (
                      <div key={j} className="text-[11px] font-mono bg-violet-50 text-violet-700 px-2 py-1 rounded">
                        <span className="font-semibold">{tc.name ?? tc.type}</span>
                        {tc.input && <span className="text-violet-500"> {JSON.stringify(tc.input).slice(0, 120)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
      {freezeAt !== null && (
        <FreezeModal
          workflowId={workflowId}
          messageIndex={freezeAt}
          defaultName={`snap@${freezeAt}`}
          onClose={() => setFreezeAt(null)}
        />
      )}
    </div>
  )
}

export default function AdminProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const project = useQuery(api.admin.getProject, id ? { id } : 'skip')
  const launchAnalysis = useAction(api.admin.launchSessionAnalysis)
  const sessionFrames = useQuery(
    api.sessionRecordings.adminGetFramesByWorkflow,
    project?._id ? { workflowId: project._id } : 'skip'
  )
  const feedback = useQuery(
    api.exportFeedback.getForWorkflow,
    project?._id ? { workflowId: project._id } : 'skip'
  )
  const [analyzing, setAnalyzing] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'summary' | 'messages' | 'chat' | 'video' | 'log' | 'behavior' | 'screenshot'>('summary')

  if (project === undefined) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }

  if (project === null) {
    return (
      <div className="p-6">
        <Link to="/admin/videos" className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 mb-6">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <p className="text-sm text-neutral-500 text-center py-8">Video not found</p>
      </div>
    )
  }

  const analysis = (project as any).sessionAnalysis as {
    status: string; score?: number; title?: string; summary?: string; content?: string;
    screenshotUrl?: string; analyzedAt: number; pipelineVersion?: number;
    dimensions?: Array<{ name: string; score: number; weight: number; reasoning: string }>;
    issues?: Array<{ category: string; severity: 'minor' | 'moderate' | 'severe'; description: string; evidence: string; timestampMs?: number; screenshotIndex?: number }>;
    screenshotCount?: number; iterationCount?: number;
  } | undefined
  const isRunning = analyzing || analysis?.status === 'in-progress'
  const sessionIds = (project.posthogSessionIds || []) as string[]

  const noteContent = analysis?.content || analysis?.summary || ''
  const behaviorIdx = noteContent.indexOf('\n---BEHAVIOR---\n')
  const chatIdx = noteContent.indexOf('\n---CHAT---\n')
  const videoIdx = noteContent.indexOf('\n---VIDEO---\n')
  const notesIdx = noteContent.indexOf('\n---NOTES---\n')
  const logIdx = noteContent.indexOf('\n---LOG---\n')

  const sectionEnd = (after: number) => {
    const candidates = [behaviorIdx, chatIdx, videoIdx, notesIdx, logIdx].filter(i => i > after)
    return candidates.length > 0 ? Math.min(...candidates) : undefined
  }

  const summary = analysis?.summary || (chatIdx >= 0 ? noteContent.slice(0, chatIdx) : noteContent.split('\n---\n')[0] || noteContent)
  const chatTranscript = chatIdx >= 0
    ? noteContent.slice(chatIdx + 12, sectionEnd(chatIdx))
    : null
  const behavioralData = behaviorIdx >= 0
    ? noteContent.slice(behaviorIdx + 16, sectionEnd(behaviorIdx))
    : null
  const videoAnalysis = videoIdx >= 0
    ? noteContent.slice(videoIdx + 13, sectionEnd(videoIdx))
    : null
  const detailedLog = logIdx >= 0
    ? noteContent.slice(logIdx + 10)
    : noteContent.includes('\n---\n') ? noteContent.split('\n---\n').slice(1).join('\n---\n').replace(/^Detailed analysis log:\n/, '') : null

  const score = analysis?.score ?? null

  const handleAnalyze = () => {
    if (isRunning) return
    setAnalysisError(null)
    setAnalyzing(true)
    setActiveTab('log')
    launchAnalysis({ workflowId: project._id })
      .then((res: any) => {
        setAnalyzing(false)
        setActiveTab('summary')
        if (res?.error) setAnalysisError(res.error)
      })
      .catch((err: any) => {
        setAnalyzing(false)
        setAnalysisError(err.message)
      })
  }

  return (
    <div className="p-5 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between py-2 max-w-[1070px]">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/admin/videos" className="text-neutral-400 hover:text-neutral-600 transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          {project.thumbnailUrl ? (
            <img src={project.thumbnailUrl} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0 bg-neutral-100" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-neutral-100 flex items-center justify-center shrink-0">
              <FolderOpen className="h-5 w-5 text-neutral-400" />
            </div>
          )}
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg font-semibold text-neutral-900 truncate">{project.title || 'Untitled'}</h1>
              {project.shortcode && <span className="text-[11px] font-mono text-neutral-400">{project.shortcode}</span>}
              <PlanBadge plan={project.effectivePlan || 'free'} />
            </div>
            <div className="flex items-center gap-2 text-[13px] text-neutral-500">
              <span>{project.userEmail || 'No email'}</span>
              <span className="text-neutral-300">|</span>
              <span>{formatDate(project.createdAt)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {score !== null && <ScoreBadge score={score} />}
          <Link
            to={`/admin/videos/inspect/${id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-neutral-100 text-neutral-700 text-xs font-medium hover:bg-neutral-200 transition-colors"
          >
            <MessageSquare className="h-3 w-3" />
            Inspect
          </Link>
          <a
            href={`/video/${project.shortcode ?? project._id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-neutral-900 text-white text-xs font-medium hover:bg-neutral-800 transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            Open Project
          </a>
        </div>
      </div>

      {/* Main Layout: Video + Stats */}
      <div className="grid grid-cols-[680px_1fr] gap-3 overflow-hidden max-w-[1070px]">
        {/* Left: Video Player + Tabs */}
        <div className="space-y-3 overflow-hidden">
          <div className="rounded-xl overflow-hidden bg-black max-h-[640px] flex items-center justify-center">
            {project.hasExported && project.finalVideoUrl ? (
              <PlyrPlayer
                src={project.finalVideoUrl}
                poster={project.thumbnailUrl || undefined}
                className="w-full max-h-[640px]"
              />
            ) : project.thumbnailUrl ? (
              <div className="relative w-full aspect-video">
                <img src={project.thumbnailUrl} alt="" className="w-full h-full object-contain" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <div className="text-xs text-white/70 bg-black/50 px-3 py-1.5 rounded-full">
                    {project.hasExported ? 'Export processing...' : 'No export available'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full aspect-video flex items-center justify-center">
                <Film className="h-10 w-10 text-neutral-700" />
              </div>
            )}
          </div>

          {/* Tabs */}
          <div>
            <div className="flex items-center gap-1 border-b border-neutral-200">
              {([
                { id: 'summary' as const, label: 'Summary' },
                { id: 'messages' as const, label: 'Messages' },
                { id: 'chat' as const, label: 'Chat', hidden: !chatTranscript && !isRunning },
                { id: 'video' as const, label: 'Video', hidden: !videoAnalysis && !isRunning },
                { id: 'log' as const, label: 'Analysis Log' },
                { id: 'behavior' as const, label: 'Behavior', hidden: !behavioralData && !isRunning },
                { id: 'screenshot' as const, label: 'Screenshot' },
              ] as const).filter(t => !('hidden' in t && t.hidden)).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-neutral-900 text-neutral-900'
                      : 'border-transparent text-neutral-400 hover:text-neutral-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              <div className="flex-1" />
              <button
                onClick={handleAnalyze}
                disabled={isRunning}
                className={`flex items-center gap-1.5 px-2.5 py-1 mb-1 rounded text-[11px] font-medium transition-colors ${
                  isRunning ? 'opacity-50 cursor-not-allowed text-neutral-400' :
                  analysis?.status === 'completed' ? 'text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100' :
                  'bg-amber-100 text-amber-800 hover:bg-amber-200'
                }`}
              >
                {isRunning ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                {analysis?.status === 'completed' ? 'Re-analyze' : 'Analyze'}
              </button>
            </div>

            {analysisError && (
              <div className="mt-2 rounded-lg bg-red-50 p-3 text-xs text-red-700">
                Analysis failed: {analysisError}
              </div>
            )}

            <div className="mt-2">
              {activeTab === 'summary' && (
                <>
                  {isRunning && analysis?.status === 'in-progress' && (
                    <AnalysisLog content={analysis.summary || ''} />
                  )}
                  {!isRunning && summary ? (
                    <div className="space-y-4">
                      {/* Structured data for v2 analyses */}
                      {analysis?.pipelineVersion && analysis.pipelineVersion >= 2 && (
                        <div className="space-y-4">
                          {analysis.dimensions && analysis.dimensions.length > 0 && (
                            <div className="rounded-lg border border-neutral-200 bg-white p-3.5">
                              <div className="text-xs font-medium text-neutral-500 mb-2">Dimension Scores</div>
                              <DimensionScores dimensions={analysis.dimensions} />
                              {analysis.iterationCount !== undefined && (
                                <div className="mt-2 text-[11px] text-neutral-400">
                                  {analysis.iterationCount} agent iterations, {analysis.screenshotCount ?? 0} screenshots taken
                                </div>
                              )}
                            </div>
                          )}
                          {analysis.issues && analysis.issues.length > 0 && (
                            <div className="rounded-lg border border-neutral-200 bg-white p-3.5">
                              <div className="text-xs font-medium text-neutral-500 mb-2">Findings ({analysis.issues.length})</div>
                              <IssuesList issues={analysis.issues} />
                            </div>
                          )}
                        </div>
                      )}
                      <pre className="rounded-lg bg-black text-white text-[13px] p-5 leading-relaxed font-mono whitespace-pre-wrap overflow-y-auto max-h-[600px]">{summary}</pre>
                    </div>
                  ) : !isRunning && !summary && (
                    <div className="text-center py-12 text-sm text-neutral-400">
                      Click Analyze to generate a session analysis
                    </div>
                  )}
                </>
              )}
              {activeTab === 'messages' && (
                <MessagesTab workflowId={project._id} />
              )}
              {activeTab === 'chat' && (
                chatTranscript ? (
                  <ChatLog content={chatTranscript} />
                ) : (
                  <div className="text-center py-12 text-sm text-neutral-400">
                    No chat transcript available
                  </div>
                )
              )}
              {activeTab === 'video' && (
                videoAnalysis ? (
                  <ConsoleBlock content={videoAnalysis} />
                ) : (
                  <div className="text-center py-12 text-sm text-neutral-400">
                    No video analysis available
                  </div>
                )
              )}
              {activeTab === 'log' && (
                <>
                  {isRunning && analysis?.status === 'in-progress' ? (
                    <AnalysisLog content={analysis.summary || ''} />
                  ) : detailedLog ? (
                    <AnalysisLog content={detailedLog} />
                  ) : (
                    <div className="text-center py-12 text-sm text-neutral-400">
                      No analysis log available
                    </div>
                  )}
                </>
              )}
              {activeTab === 'behavior' && (
                behavioralData ? (
                  <ConsoleBlock content={behavioralData} />
                ) : (
                  <div className="text-center py-12 text-sm text-neutral-400">
                    No behavioral data available — run analysis on a project with PostHog recordings
                  </div>
                )
              )}
              {activeTab === 'screenshot' && (
                sessionFrames && sessionFrames.length > 0 ? (
                  <div className="space-y-3">
                    <div className="text-xs text-neutral-500">
                      {sessionFrames.length} frames captured from session recording
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {sessionFrames.map((frame, i) => (
                        <a
                          key={i}
                          href={frame.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group relative rounded-lg overflow-hidden border border-neutral-200 bg-neutral-50 hover:border-neutral-400 transition-colors"
                        >
                          <img
                            src={frame.url}
                            alt={`Session frame at ${formatFrameTimestamp(frame.relativeMs)}`}
                            className="w-full aspect-video object-cover"
                            loading="lazy"
                          />
                          <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-0.5 text-[10px] font-mono text-white/90">
                            {formatFrameTimestamp(frame.relativeMs)}
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : analysis?.screenshotUrl ? (
                  <div className="rounded-lg overflow-hidden border border-neutral-200 bg-neutral-50">
                    <img
                      src={analysis.screenshotUrl}
                      alt="Project screenshot"
                      className="w-full h-auto"
                    />
                  </div>
                ) : (
                  <div className="text-center py-12 text-sm text-neutral-400">
                    No screenshots available — run analysis to capture session frames
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        {/* Right: Stats Panel */}
        <div className="space-y-2">
          {/* Session Stats */}
          <div className="rounded-lg border border-neutral-200 bg-white px-3.5 py-3 space-y-0.5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2">Session</div>
            <Stat icon={Clock} label="Duration" value={formatDuration(project.sessionDurationMs)} />
            <Stat
              icon={MessageSquare}
              label="Messages"
              value={project.messageCount}
              sub={`${project.userMessageCount} user, ${project.agentMessageCount} agent`}
            />
            <Stat icon={Wrench} label="Tool Calls" value={project.toolCallCount} />
          </div>

          {/* Clip Stats */}
          <div className="rounded-lg border border-neutral-200 bg-white px-3.5 py-3 space-y-0.5">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2">Output</div>
            <Stat
              icon={Film}
              label="Clips"
              value={`${project.generatedClipCount}/${project.clipCount}`}
              sub={project.failedClipCount > 0 ? `${project.failedClipCount} failed` : 'generated'}
            />
            <Stat
              icon={project.hasExported ? Download : Play}
              label="Export"
              value={project.hasExported ? 'Completed' : project.finalVideoStatus || 'None'}
            />
            {project.hasCharacters && (
              <Stat
                icon={Users}
                label="Characters"
                value={project.characterCount}
                sub={project.characterNames?.join(', ')}
              />
            )}
            {project.hasScript && (
              <Stat
                icon={FileText}
                label="Script"
                value="Yes"
                sub={project.narrative ? `${project.narrative.slice(0, 60)}...` : undefined}
              />
            )}
          </div>

          {/* User Feedback */}
          {feedback && feedback.filter(f => !f.skipped).length > 0 && (
            <div className="rounded-lg border border-neutral-200 bg-white px-3.5 py-3 space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2">User Feedback</div>
              {feedback.filter(f => !f.skipped).map((fb) => (
                <div key={fb._id} className="space-y-1">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={12} className={s <= fb.rating ? 'fill-amber-400 text-amber-400' : 'text-neutral-300'} />
                    ))}
                    <span className="text-[11px] text-neutral-400 ml-1">{fb.rating}/5</span>
                  </div>
                  {fb.feedback && (
                    <p className="text-[12px] text-neutral-600 leading-relaxed">{fb.feedback}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Status */}
          <div className="rounded-lg border border-neutral-200 bg-white px-3.5 py-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2">Status</div>
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                project.status === 'completed' ? 'bg-emerald-500' :
                (project.status as string) === 'error' ? 'bg-red-500' :
                'bg-amber-500'
              }`} />
              <span className="text-sm font-medium text-neutral-900 capitalize">{project.status}</span>
            </div>
          </div>

          {/* Session Replays */}
          {sessionIds.length > 0 && (
            <div className="rounded-lg border border-neutral-200 bg-white px-3.5 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 mb-2">
                Replays ({sessionIds.length})
              </div>
              <div className="space-y-1">
                {[...sessionIds].reverse().slice(0, 3).map((sid, i) => (
                  <Link
                    key={sid}
                    to={`/admin/videos/replay/${sid}`}
                    className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-neutral-50 transition-colors group text-xs"
                  >
                    <div className="flex items-center gap-1.5">
                      <Eye className="h-3 w-3 text-neutral-300" />
                      <span className="text-neutral-500">Session {sessionIds.length - i}</span>
                    </div>
                    <ArrowRight className="h-3 w-3 text-neutral-300 group-hover:text-neutral-500 transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
